<template>
    <v-dialog :value="show" width="680" persistent scrollable>
        <div class="wl">
            <div class="wl-head">
                <div>
                    <div class="wl-title">
                        {{ isEdit ? $t('media.wordlist.title_edit') : $t('media.wordlist.title_new') }}
                    </div>
                    <div class="wl-sub">{{ purposeLabel }}</div>
                </div>
                <v-btn icon small :disabled="isSaving" @click="close">
                    <v-icon size="20">mdi-close</v-icon>
                </v-btn>
            </div>

            <div class="wl-body">
                <!-- ⚠ 理論上呼叫端不該對非字表開這個對話框，但擋一次比讓老師打完 20 個字
                     才被後端 400 好。 -->
                <div v-if="!isWordlist" class="wl-blocked">
                    <v-icon size="30" color="#e57373">mdi-information-outline</v-icon>
                    <div>{{ $t('media.wordlist.not_wordlist') }}</div>
                </div>

                <template v-else>
                    <!-- ── 單字 ─────────────────────────────────────────── -->
                    <div class="wl-field">
                        <!-- ⚠ 這裡曾經有一顆「填入 9 個顏色詞」的快速鍵（2026-09-01 移除）。
                             移除的理由是它把老師往錯的方向推：那 9 個是**螢幕上會出現的墨水
                             顏色**（也就是答案），而**顯示的字完全不受限制**——照那顆按鈕填完
                             就只有 9 回合，而且看起來像是「只能填這些」。字表的長度與內容都是
                             老師的設計空間（一個字 = 一回合），不該被一顆按鈕收窄。 -->
                        <div class="wl-lbl">
                            {{ $t('media.wordlist.words') }}<span class="wl-req">*</span>
                        </div>
                        <v-textarea v-model="wordsText" dense outlined hide-details rows="6"
                            :disabled="isSaving" class="wl-ta"
                            :placeholder="$t(`media.wordlist.words_ph_${purposeKey}`)" />
                        <div class="wl-hint">{{ $t(`media.wordlist.words_rule_${purposeKey}`) }}</div>

                        <!-- 解析後的結果。**一定要有這一塊**：輸入框是自由文字，老師無法
                             從它看出「我到底輸入了幾個字」、「哪一個字不合法」。
                             ⚠ 找單字在這裡顯示**轉大寫後**的樣子（後端一律轉大寫），
                             但不動輸入框裡的字——邊打字邊被改寫是很糟的體驗。 -->
                        <div v-if="parsed.length" class="wl-preview">
                            <div class="wl-preview-head">
                                <span>{{ $t('media.wordlist.parsed', { n: parsed.length }) }}</span>
                                <span class="wl-cnt">{{ parsed.length }} / {{ MAX_WORDS }}</span>
                            </div>
                            <div class="wl-chips">
                                <span v-for="(w, i) in displayWords" :key="i" class="wl-chip"
                                    :class="{ 'wl-chip--bad': w.bad }" :title="w.reason">
                                    {{ w.text }}
                                    <v-icon v-if="w.bad" size="13" color="#c62828">
                                        mdi-alert-circle
                                    </v-icon>
                                </span>
                            </div>
                        </div>

                        <div v-if="wordsError" class="wl-err">{{ wordsError }}</div>

                        <!-- 軟性提醒（不擋送出）：字比預設格子長時，用到這份字表的題目
                             必須把格子調大，否則那個字**靜默地**放不進格子。
                             ⚠ 難度是題目設定而不是素材屬性，所以這裡只能提醒不能擋。 -->
                        <div v-else-if="gridWarnWord" class="wl-warn">
                            <v-icon size="16" color="#ef6c00">mdi-alert-outline</v-icon>
                            <span>{{ $t('media.wordlist.warn_grid', {
                                w: gridWarnWord, n: gridWarnWord.length, d: DEFAULT_GRID,
                            }) }}</span>
                        </div>
                    </div>

                    <!-- ── 標題 / 說明 ──────────────────────────────────── -->
                    <div class="wl-field">
                        <div class="wl-lbl">
                            {{ $t('media.wordlist.name') }}<span class="wl-req">*</span>
                        </div>
                        <v-text-field v-model="title" dense outlined hide-details
                            :maxlength="TITLE_MAX" :disabled="isSaving"
                            :placeholder="$t('media.wordlist.name_ph')" />
                        <div class="wl-cnt">{{ title.length }} / {{ TITLE_MAX }}</div>
                    </div>

                    <div class="wl-field">
                        <div class="wl-lbl">{{ $t('media.wordlist.desc') }}</div>
                        <v-textarea v-model="description" dense outlined hide-details rows="2"
                            :maxlength="DESC_MAX" :disabled="isSaving"
                            :placeholder="$t('media.wordlist.desc_ph')" />
                        <div class="wl-cnt">{{ description.length }} / {{ DESC_MAX }}</div>
                    </div>

                    <!-- ── 分享範圍 ─────────────────────────────────────── -->
                    <div class="wl-field">
                        <div class="wl-lbl">{{ $t('media.wordlist.scope') }}</div>
                        <v-radio-group v-model="shareScope" hide-details dense class="wl-radios"
                            :disabled="isSaving">
                            <v-radio v-for="s in scopes" :key="s" :value="s">
                                <template #label>
                                    <div>
                                        <div class="wl-radio-lbl">{{ $t(`media.upload.scope_${s}`) }}</div>
                                        <!-- ⚠ private 的文案**不能寫「只有自己看得到」**（ADR 0007）。
                                             字表沒有檔案所以沒有 S3 物件可讀，但文案與其他素材
                                             共用同一組 key，改一邊就兩邊不一致。 -->
                                        <div class="wl-radio-note">{{ $t(`media.upload.scope_${s}_note`) }}</div>
                                    </div>
                                </template>
                            </v-radio>
                        </v-radio-group>

                        <div v-if="shareScope === 'public'" class="wl-review">
                            <v-icon size="18" color="#1e88e5">mdi-shield-check-outline</v-icon>
                            <div>
                                <div>{{ $t('media.upload.review_notice') }}</div>
                                <div class="wl-review-ok">{{ $t('media.upload.review_still_usable') }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- ── 權利聲明（只有建立時）────────────────────────── -->
                    <!-- ⚠ **字表也要勾**（決策 19）。手打的字表多半是老師自己的，但字表也
                         可能是從教科書某一課整理出來的，而公開的字表要送審——三條建立路徑
                         裡只有字表沒有權利記錄的話，審核的人就沒有來源可看。 -->
                    <div v-if="!isEdit" class="wl-field wl-rights">
                        <v-checkbox v-model="rightsAgreed" dense hide-details class="wl-rights-box"
                            :disabled="isSaving">
                            <template #label>
                                <span class="wl-rights-lbl">{{ $t('media.wordlist.rights_agree') }}</span>
                            </template>
                        </v-checkbox>
                        <v-text-field v-model="rightsNote" dense outlined hide-details
                            :maxlength="NOTE_MAX" :disabled="isSaving"
                            :placeholder="$t('media.wordlist.rights_note_ph')" />
                        <div class="wl-hint">{{ $t('media.wordlist.rights_note_hint') }}</div>
                    </div>

                    <!-- ⚠ 編輯模式的送出**可能因為別的題目而失敗**，而那不是一般錯誤：
                         老師要知道是哪一道題目卡住、要先去改什麼。所以錯誤區塊在編輯模式
                         下要顯示後端的原文（見 submitError 的說明）。 -->
                    <div v-if="submitError" class="wl-err wl-err--block">{{ submitError }}</div>
                    <div v-if="submitDetail" class="wl-err-detail">{{ submitDetail }}</div>
                </template>
            </div>

            <div class="wl-foot">
                <v-btn text class="normal-case" :disabled="isSaving" @click="close">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn color="primary" depressed class="normal-case"
                    :disabled="!canSubmit" :loading="isSaving" @click="submit">
                    {{ isEdit ? $t('button.save') : $t('media.wordlist.submit') }}
                </v-btn>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import { mediaServices } from '@/services/media';
import {
    SHARE_SCOPES, WORDLIST,
    kindOfPurpose, splitWords, checkWords, wordsOf,
} from './mediaConstants';

/// 與後端 `AddWordlistMediaAction` 的 StringValidator 上限一致
const TITLE_MAX = 200;
const DESC_MAX = 500;
const NOTE_MAX = 500;
/// 找單字的預設格子邊長（後端 `QuestMode.DEFAULT_GRID_SIZE`）。只用來出軟性提醒。
const DEFAULT_GRID = 11;

/**
 * 字表素材的建立與編輯 —— 見 mangachat-studio-api docs/plans/MediaLibrary.spec.md §2.2、§5.2。
 *
 * ⚠ **字表是唯一「在介面上建立」而不是上傳的素材。** 它沒有檔案，內容整包在
 * `attributes.words`，所以走 JSON 端點（`POST /diary/media/wordlist`）、不佔容量配額、
 * 不轉檔。`MediaUploadDialog` 那一支對字表會被後端明確拒絕。
 *
 * ⚠ **沒有語系欄位**（2026-09-01 決定）：字表目前只支援英文，`lang` 沒有可分的東西，
 * 所以建立時一律不送、由後端寫 NULL（＝通用素材）。⚠ **不要「順手」改成固定送 `'en'`**
 * ——挑選器若哪天恢復語系過濾，`lang = 'en'` 的字表在中文介面下會憑空消失，而
 * `NULL` 的通用素材不論過濾都撈得到。要分語系是新的一張票，不是一個預設值。
 *
 * ⚠ **也是唯一「內容可以就地改變」的素材**，而改動會立刻影響已經在用它的題目。
 * 所以編輯模式的送出**可能被後端拒絕**（見 `submit`），那是正常路徑而不是錯誤。
 *
 * ⚠ **兩個 purpose 的規則不一樣，不能只寫一套。** 找單字的字要被藏進字母格
 * （只能 A–Z、2–15 字母，全都是 `WordSearch.vue` 的硬限制），講顏色的字只是顯示用的
 * （不限字元、1–24 字）。三個限制**違反的後果都是靜默的**——不報錯、不過關、學生卡死，
 * 所以 UI 一定要在送出前就講清楚。
 */
export default {
    name: 'WordlistDialog',
    props: {
        /** 對話框開關，用 `.sync`。 */
        show: { type: Boolean, default: false },
        /**
         * 建立時必填：`wordsearch` / `colorword`。
         * ⚠ 編輯時忽略它，一律用 `media.purpose`——`purpose` 不可改。
         */
        purpose: { type: String, default: '' },
        /**
         * 要編輯的字表。給了就是編輯模式。
         * ⚠ 呼叫端要先確認 `is_mine`：官方素材與別人的素材後端回 403。
         */
        media: { type: Object, default: null },
    },
    data() {
        return {
            TITLE_MAX,
            DESC_MAX,
            NOTE_MAX,
            DEFAULT_GRID,
            MAX_WORDS: WORDLIST.MAX_WORDS,
            scopes: SHARE_SCOPES,
            wordsText: '',
            title: '',
            description: '',
            shareScope: 'private',
            rightsAgreed: false,
            rightsNote: '',
            submitError: '',
            submitDetail: '',
            isSaving: false,
            /// 編輯模式的初始快照，用來判斷「哪幾支端點要打」（見 submit）
            initial: { words: '', title: '', description: '', shareScope: 'private' },
        };
    },
    computed: {
        isEdit() {
            return !!(this.media && this.media.media_id);
        },
        /** ⚠ 編輯時 purpose 一律從素材讀，不看 prop——purpose 不可改。 */
        effectivePurpose() {
            return this.isEdit ? this.media.purpose : this.purpose;
        },
        isWordlist() {
            return kindOfPurpose(this.effectivePurpose) === 'wordlist';
        },
        /**
         * i18n key 的後綴。
         * ⚠ 不認識的字表 purpose（未來會有，規格 §2.5）退回講顏色那一組文案——
         * 它是兩者裡限制較寬的，比顯示一組講錯的硬限制安全。
         */
        purposeKey() {
            return this.effectivePurpose === 'wordsearch' ? 'wordsearch' : 'colorword';
        },
        /** ⚠ 不認識的 purpose 顯示原值而不是 raw key（規格 §2.5 的容忍規則）。 */
        purposeLabel() {
            const key = `media.purpose.${this.effectivePurpose}`;
            return this.$te(key) ? this.$t(key) : this.effectivePurpose;
        },
        /**
         * 解析後的單字。**唯一的真相來源**，計數、驗證與送出都用它。
         *
         * ⚠ **只有找單字去重**，與後端 `WordlistValidator.parseWords` 一致：
         * 講顏色的**一個字 = 一個回合**（每回合的墨水顏色重新隨機挑），所以
         * `['red','red','red']` 是三個不同答案的回合——重複是老師拉長遊戲的手段，
         * 去重會靜默把它吃掉。找單字相反：重複只是白佔格子，還會讓
         * 「要找幾個字 ≤ 字表字數」那個上界虛胖。
         *
         * ⚠ 前端**必須跟後端一致**，否則計數會騙人：預覽說「共 4 個字」而實際只存了 3 個。
         */
        parsed() {
            const words = splitWords(this.wordsText);
            if (this.effectivePurpose !== 'wordsearch') {
                return words;
            }
            const seen = new Set();
            return words.filter((w) => {
                const key = w.toUpperCase();
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            });
        },
        /** 驗證結果，沒問題回空字串。 */
        wordsError() {
            if (!this.parsed.length) {
                /// 還沒打字不算錯——不要一開啟就紅字。
                return '';
            }
            const bad = checkWords(this.parsed, this.effectivePurpose);
            return bad ? this.$t(bad.key, bad.params) : '';
        },
        /**
         * 顯示用的單字（含「哪一個不合法」）。
         * ⚠ 找單字顯示**轉大寫後**的樣子（後端一律轉大寫），但不動輸入框——
         * 邊打字邊被改寫是很糟的體驗。
         */
        displayWords() {
            const isWs = this.effectivePurpose === 'wordsearch';
            return this.parsed.map((raw) => {
                const text = isWs ? raw.toUpperCase() : raw;
                const bad = checkWords([raw], this.effectivePurpose);
                return {
                    text: text,
                    bad: !!bad,
                    reason: bad ? this.$t(bad.key, bad.params) : '',
                };
            });
        },
        /**
         * 比預設格子長的那個字（軟性提醒，不擋送出）。
         *
         * ⚠ **難度是題目設定而不是素材屬性**（規格 §2.2），同一份字表可以被
         * `grid_size` 不同的題目共用，所以這裡只能提醒。真正的閘門在存題目時
         * （後端 `assertWordsFitGrid`）。
         */
        gridWarnWord() {
            if (this.effectivePurpose !== 'wordsearch') {
                return '';
            }
            const long = this.parsed.filter((w) => w.length > DEFAULT_GRID);
            if (!long.length) {
                return '';
            }
            /// 只講最長的那一個：一次列出全部太吵。
            return long.reduce((a, b) => (b.length > a.length ? b : a), '').toUpperCase();
        },
        canSubmit() {
            if (!this.isWordlist || this.isSaving) {
                return false;
            }
            if (!this.parsed.length || this.wordsError || !this.title.trim()) {
                return false;
            }
            /// ⚠ 權利聲明只在建立時是閘門（決策 19）。編輯已存在的字表不必重勾——
            /// `rights_declared_at` 已經寫了，重新要求只是噪音。
            return this.isEdit ? this.isDirty : this.rightsAgreed;
        },
        /** 編輯模式：有沒有真的改東西。⚠ 沒改就不該打任何 API。 */
        isDirty() {
            return this.wordsJoined !== this.initial.words
                || this.title.trim() !== this.initial.title
                || this.description.trim() !== this.initial.description
                || this.shareScope !== this.initial.shareScope;
        },
        /** 用來比對「單字有沒有變」的正規化字串。 */
        wordsJoined() {
            return this.parsed.join('\n');
        },
    },
    watch: {
        /**
         * ⚠ **`immediate: true` 不能省。** 呼叫端多半用 `v-if` 才把這個元件建出來，
         * 那種情況下元件掛載時 `show` 已經是 true，一般的 watcher 不會觸發——
         * 於是第一次打開是空的、第二次才正常。症狀是靜默的空表單。
         * （同一個坑在 MediaPicker / MediaUploadDialog / MediaEditDialog 都踩過。）
         */
        show: {
            immediate: true,
            handler(opened) {
                if (opened) {
                    this.reset();
                }
            },
        },
    },
    methods: {
        reset() {
            this.submitError = '';
            this.submitDetail = '';
            this.isSaving = false;

            if (this.isEdit) {
                const words = wordsOf(this.media);
                this.wordsText = words.join('\n');
                this.title = this.media.title || '';
                this.description = this.media.description || '';
                this.shareScope = this.media.share_scope || 'private';
                /// 編輯不重勾權利聲明（見 canSubmit）
                this.rightsAgreed = true;
                this.rightsNote = '';
                this.initial = {
                    words: words.join('\n'),
                    title: this.title.trim(),
                    description: this.description.trim(),
                    shareScope: this.shareScope,
                };
                return;
            }

            this.wordsText = '';
            this.title = '';
            this.description = '';
            this.shareScope = 'private';
            this.rightsAgreed = false;
            this.rightsNote = '';
            this.initial = { words: '', title: '', description: '', shareScope: 'private' };
        },

        /**
         * 送出。
         *
         * ⚠ **編輯模式最多打兩支端點，而且順序不能反**：
         * 1. `updateWordlistWords` —— **可能被拒絕**（引用它的題目難度設定不相容）
         * 2. `updateMedia` —— 只改中介資料，一定成功
         *
         * 先打會失敗的那一支，失敗時什麼都還沒改。反過來的話，單字被拒絕但標題已經
         * 改掉了，老師會看到一個「失敗了但東西變了一半」的狀態。
         *
         * ⚠ **沒改的欄位不打對應的端點。** 尤其是單字：重送同一份內容仍然會觸發
         * 引用題目的重驗，於是「只改標題」可能因為別人的題目而失敗。
         */
        async submit() {
            if (!this.canSubmit) {
                return;
            }
            this.isSaving = true;
            this.submitError = '';
            this.submitDetail = '';
            try {
                if (!this.isEdit) {
                    const res = await mediaServices.addWordlistMedia({
                        purpose: this.effectivePurpose,
                        words: this.parsed,
                        title: this.title.trim(),
                        description: this.description.trim() || null,
                        shareScope: this.shareScope,
                        rightsAgreed: this.rightsAgreed,
                        rightsNote: this.rightsNote.trim() || null,
                    });
                    this.$emit('created', res);
                    this.$emit('update:show', false);
                    return;
                }

                let updated = this.media;
                if (this.wordsJoined !== this.initial.words) {
                    updated = await mediaServices.updateWordlistWords(
                        this.media.media_id, this.parsed);
                }
                if (this.title.trim() !== this.initial.title
                    || this.description.trim() !== this.initial.description
                    || this.shareScope !== this.initial.shareScope) {
                    /// ⚠ **description 一定要一起送**：後端對「沒帶」與「帶空字串」都算出
                    /// null，而 service 只檢查 `!== undefined`，所以沒帶就是**清空**。
                    updated = await mediaServices.updateMedia(this.media.media_id, {
                        title: this.title.trim(),
                        description: this.description.trim() || null,
                        shareScope: this.shareScope,
                    });
                    /// ⚠ `updateMedia` 的回傳**不含 attributes 以外的字表內容變更**——
                    /// 它讀的是資料庫當下的值，所以剛剛改過的 words 也在裡面。
                    /// 兩支都打時以後打的那一筆為準是對的。
                }
                this.$emit('saved', updated);
                this.$emit('update:show', false);
            } catch (err) {
                this.setError(err);
            } finally {
                this.isSaving = false;
            }
        },

        /**
         * ⚠ **編輯模式的 400 要顯示後端原文。**
         *
         * 一般的 400 我們都翻成中文，因為後端訊息是給開發者看的技術描述。但這一支的
         * 400 是「**哪一道題目**因為**什麼設定**而不相容」，訊息裡有題目 id 與標題——
         * 那是老師唯一能拿到的線索，翻成「儲存失敗」等於把它丟掉。所以中文說明 +
         * 原文細節兩者都顯示。
         */
        setError(err) {
            const status = err && err.status;
            if (status === 403) {
                this.submitError = this.$t('media.wordlist.err_forbidden');
                return;
            }
            if (status === 400) {
                console.log('wordlist rejected:', err && err.message);
                this.submitError = this.isEdit
                    ? this.$t('media.wordlist.err_in_use')
                    : this.$t('media.wordlist.err_rejected');
                this.submitDetail = String((err && err.message) || '');
                return;
            }
            this.submitError = this.$t('media.wordlist.err_failed');
        },

        close() {
            if (this.isSaving) {
                return;
            }
            this.$emit('update:show', false);
        },
    },
};
</script>

<style scoped>
.wl {
    background: #fff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    max-height: 88vh;
}

.wl-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px 20px 12px;
    border-bottom: 1px solid #eceff1;
}

.wl-title {
    font-size: 17px;
    font-weight: 600;
    color: #263238;
}

.wl-sub {
    font-size: 12px;
    color: #78909c;
    margin-top: 2px;
}

.wl-body {
    padding: 16px 20px;
    overflow-y: auto;
}

.wl-field {
    margin-bottom: 18px;
}

.wl-lbl {
    font-size: 13px;
    font-weight: 600;
    color: #455a64;
    margin-bottom: 6px;
}

.wl-req {
    color: #e53935;
}

/* 一行一個字，等寬字體讓「這個字有幾個字母」看得出來——找單字的長度限制就是字母數 */
.wl-ta ::v-deep textarea {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 13px;
    line-height: 1.6;
}

.wl-hint {
    font-size: 12px;
    color: #90a4ae;
    margin-top: 4px;
    line-height: 1.5;
}

.wl-cnt {
    font-size: 11px;
    color: #b0bec5;
    text-align: right;
    margin-top: 2px;
}

.wl-preview {
    margin-top: 8px;
    padding: 8px 10px;
    background: #f5f7f8;
    border-radius: 4px;
}

.wl-preview-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    font-size: 11px;
    color: #78909c;
    margin-bottom: 6px;
}

/* 這一塊的計數已經在 head 裡，不要再吃 .wl-cnt 的 margin */
.wl-preview-head .wl-cnt {
    margin-top: 0;
}

.wl-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.wl-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 12px;
    line-height: 1;
    padding: 5px 7px;
    border-radius: 3px;
    background: #fff;
    border: 1px solid #cfd8dc;
    color: #37474f;
}

.wl-chip--bad {
    border-color: #ef9a9a;
    background: #ffebee;
    color: #c62828;
}

.wl-err {
    font-size: 12px;
    color: #e53935;
    margin-top: 6px;
    line-height: 1.5;
}

.wl-err--block {
    padding: 8px 10px;
    background: #ffebee;
    border-radius: 4px;
}

/* 後端原文：字級小一點、可換行，讓它看起來是「細節」而不是主要訊息 */
.wl-err-detail {
    font-size: 11px;
    color: #b71c1c;
    margin-top: 4px;
    line-height: 1.5;
    word-break: break-word;
}

.wl-warn {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    color: #795548;
    background: #fff8e1;
    border-radius: 4px;
    padding: 8px 10px;
    margin-top: 6px;
    line-height: 1.5;
}

.wl-radios {
    margin-top: 0;
}

.wl-radio-lbl {
    font-size: 13px;
    color: #263238;
}

.wl-radio-note {
    font-size: 11px;
    color: #90a4ae;
    line-height: 1.4;
}

.wl-review {
    display: flex;
    gap: 8px;
    padding: 10px 12px;
    background: #e3f2fd;
    border-radius: 4px;
    font-size: 12px;
    color: #37474f;
    line-height: 1.5;
    margin-top: 6px;
}

.wl-review-ok {
    color: #1565c0;
    font-weight: 600;
    margin-top: 2px;
}

.wl-rights {
    padding: 12px;
    background: #fafafa;
    border-radius: 4px;
}

.wl-rights-box {
    margin-top: 0;
    margin-bottom: 8px;
}

.wl-rights-lbl {
    font-size: 13px;
    color: #263238;
    line-height: 1.5;
}

.wl-blocked {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #455a64;
    padding: 20px 0;
}

.wl-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid #eceff1;
}
</style>
